import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { AuthProvider } from './context/AuthContext';
import { FriendProvider } from './context/FriendContext';
import { NotificationProvider } from './context/NotificationContext';
import { LanguageProvider } from './context/LanguageContext';
import { PermissionsProvider } from './context/PermissionsContext';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import AdminLayout from './layouts/AdminLayout';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Encounters from './pages/Encounters';
import People from './pages/People';
import Matches from './pages/Matches';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Likes from './pages/Likes';
import Visitors from './pages/Visitors';
import VIP from './pages/VIP';
import Credits from './pages/Credits';
import Wizard from './pages/Wizard';
import VerifyEmail from './pages/VerifyEmail';
import Messages from './pages/Messages';
import ProfileDetail from './pages/ProfileDetail';
import Page from './pages/Page';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminPhotos from './pages/admin/AdminPhotos';
import AdminReports from './pages/admin/AdminReports';
import AdminFinance from './pages/admin/AdminFinance';
import AdminAds from './pages/admin/AdminAds';
import AdminRoles from './pages/admin/AdminRoles';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('auth_token');
  return isAuthenticated ? children : <Navigate to="/" replace />;
};

function App() {
  useEffect(() => {
    // iOS Safe Area Detection
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isIOSApp = navigator.standalone || window.navigator.standalone;

    if (isIOS) {
      // CSS custom property for safe area
      const safeAreaInsetBottom = window.getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)');
      if (safeAreaInsetBottom && safeAreaInsetBottom !== '0px') {
        document.documentElement.style.setProperty('--safe-area-inset-bottom', safeAreaInsetBottom);
      } else {
        // Fallback for devices without proper safe area support
        document.documentElement.style.setProperty('--safe-area-inset-bottom', '34px');
      }

      // Add iOS class for specific styling
      document.body.classList.add('ios-device');
    }

    // Orientation change listener for dynamic safe area updates
    const handleOrientationChange = () => {
      setTimeout(() => {
        if (isIOS) {
          const safeAreaInsetBottom = window.getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)');
          document.documentElement.style.setProperty('--safe-area-inset-bottom', safeAreaInsetBottom || '34px');
        }
      }, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  return (
    <LanguageProvider>
      <Router>
        <AuthProvider>
          <FriendProvider>
            <NotificationProvider>
              <PermissionsProvider>
          <Routes>
            {/* Public Routes */}
            <Route element={<AuthLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route
                path="/wizard"
                element={
                  <ProtectedRoute>
                    <Wizard />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Admin Routes */}
            <Route element={<AdminLayout />}>
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/photos"
                element={
                  <ProtectedRoute>
                    <AdminPhotos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports"
                element={
                  <ProtectedRoute>
                    <AdminReports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/finance"
                element={
                  <ProtectedRoute>
                    <AdminFinance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/ads"
                element={
                  <ProtectedRoute>
                    <AdminAds />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute>
                    <AdminRoles />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Protected Routes */}
            <Route element={<MainLayout />}>
              <Route
                path="/encounters"
                element={
                  <ProtectedRoute>
                    <Encounters />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/people"
                element={
                  <ProtectedRoute>
                    <People />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/people/:page"
                element={
                  <ProtectedRoute>
                    <People />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/matches"
                element={
                  <ProtectedRoute>
                    <Matches />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user/:id"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user/profile/:id"
                element={
                  <ProtectedRoute>
                    <ProfileDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <ProtectedRoute>
                    <Messages />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/likes"
                element={
                  <ProtectedRoute>
                    <Likes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/visitors"
                element={
                  <ProtectedRoute>
                    <Visitors />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vip"
                element={
                  <ProtectedRoute>
                    <VIP />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/credits"
                element={
                  <ProtectedRoute>
                    <Credits />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verify-email"
                element={
                  <ProtectedRoute>
                    <VerifyEmail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/page/:pageTitle"
                element={
                  <ProtectedRoute>
                    <Page />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
              </PermissionsProvider>
            </NotificationProvider>
          </FriendProvider>
        </AuthProvider>
      </Router>
    </LanguageProvider>
  );
}

export default App;

